<template>
    <BillingTabs class="mt-12" tab-key="subscriptions">
        <!-- God Mode Enterprise Plan Section -->
        <div v-if="hasGodMode" class="mb-[98px]">
            <div
                class="mt-[44px] grid-cols-layout border-b border-gray-300 pb-5 lg:grid"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ $t("vue.your_current_plan") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ $t("vue.your_current_dmarc_subscription") }}
                    </p>
                </div>

                <!-- ENTERPRISE PLAN FOR GOD MODE -->
                <div
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <h2 class="text-3xl font-bold">Enterprise Plan</h2>
                            <p class="text-md mt-2">
                                Unlimited access to all DMARCeye features with
                                no restrictions.
                            </p>
                            <!-- <div class="mt-6 grid grid-cols-2">
                                 <div class="font-bold">Domains</div>
                                 <div class="text-gray-600">Unlimited</div>
                             </div> -->
                            <div class="mt-2 grid grid-cols-2">
                                <div class="font-bold">Data History</div>
                                <div class="text-gray-600">Unlimited</div>
                            </div>
                            <div class="mt-2 grid grid-cols-2">
                                <div class="font-bold">Features</div>
                                <div class="text-gray-600">
                                    All features enabled
                                </div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div class="text-5xl font-bold text-green-600">
                                ✓ Active
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main content -->
        <div v-if="!hasGodMode" class="mb-[98px]">
            <div
                class="mt-[44px] grid-cols-layout border-b border-gray-300 pb-5 lg:grid"
                @submit.prevent="updateNameAndEmail"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ $t("vue.your_current_plan") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ $t("vue.your_current_dmarc_subscription") }}
                    </p>
                </div>

                <!-- Loading skeleton for Your Current Plan -->
                <div
                    v-if="isLoading"
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <div
                                class="h-8 w-48 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div class="mt-6 space-y-2">
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div
                                class="h-12 w-16 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-20 animate-pulse rounded bg-gray-200"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- TRIAL STATE -->
                <div
                    v-if="isTrialing && !isSubscribed && !isLoading"
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <h2 class="text-3xl font-bold">
                                {{ $t("vue.free_trial") }}
                            </h2>
                            <p class="text-md mt-2">
                                {{ $t("vue.explore_all_features_free") }}
                            </p>
                            <div class="mt-6 grid grid-cols-2">
                                <div class="font-bold">
                                    {{ $t("vue.trial_status") }}
                                </div>
                                <div class="text-gray-600">
                                    {{
                                        isTrialing
                                            ? $t("vue.active")
                                            : $t("vue.expired")
                                    }}
                                </div>
                            </div>
                            <div class="mt-2 grid grid-cols-2">
                                <div class="font-bold">
                                    {{ $t("vue.trial_expires") }}
                                </div>
                                <div class="text-gray-600">
                                    {{ trialEndsAt }}
                                </div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div class="text-5xl font-bold">
                                {{ domainsCount }}
                            </div>
                            <div>{{ $t("vue.your_domains") }}</div>
                        </div>
                    </div>
                </div>

                <!-- TRIAL ENDED STATE -->
                <div
                    v-if="!isTrialing && !isSubscribed && !isLoading"
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <h2 class="text-3xl font-bold">
                                {{ $t("vue.free_trial") }}
                            </h2>
                            <p class="text-md mt-2">
                                {{ $t("vue.explore_all_features_free") }}
                            </p>
                            <div class="mt-6 grid grid-cols-2">
                                <div class="font-bold">
                                    {{ $t("vue.trial_status") }}
                                </div>
                                <div class="text-gray-600">
                                    {{
                                        isTrialing
                                            ? $t("vue.active")
                                            : $t("vue.expired")
                                    }}
                                </div>
                            </div>
                            <div
                                v-if="trialEndsAt"
                                class="mt-2 grid grid-cols-2"
                            >
                                <div class="font-bold">
                                    {{ $t("vue.trial_expires") }}
                                </div>
                                <div class="text-gray-600">
                                    {{ trialEndsAt }}
                                </div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div class="text-5xl font-bold">
                                {{ domainsCount }}
                            </div>
                            <div>{{ $t("vue.your_domains") }}</div>
                        </div>
                    </div>
                </div>

                <!-- SUBSCRIBED STATE -->
                <div
                    v-if="isSubscribed && !isLoading"
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <h2 class="text-3xl font-bold">
                                {{
                                    currentPaddleSubscription?.items[0]?.productName
                                        ?.replace(/DMARC\/|eye\s*-\s*/g, "")
                                        .trim() || "Unknown Plan"
                                }}
                            </h2>
                            <p class="text-md mt-2">
                                {{
                                    currentPaddleSubscription?.items[0]
                                        ?.productDescription ||
                                    "No description available"
                                }}
                            </p>
                            <div class="mt-6 grid grid-cols-2">
                                <div class="font-bold">
                                    {{ $t("vue.payment_cycle") }}
                                </div>
                                <div
                                    :class="
                                        !currentPaddleSubscription?.next_billing_at
                                            ? 'text-red-500'
                                            : 'text-gray-600'
                                    "
                                >
                                    {{
                                        !currentPaddleSubscription?.next_billing_at
                                            ? $t("vue.scheduled_to_be_canceled")
                                            : currentPaddleSubscription?.period ===
                                                "year"
                                              ? $t("vue.yearly")
                                              : $t("vue.monthly")
                                    }}
                                    <span
                                        v-if="
                                            currentPaddleSubscription?.next_billing_at
                                        "
                                        class="cursor-pointer font-bold text-orange-500"
                                        @click="updateFrequency()"
                                        >{{
                                            currentPaddleSubscription?.period ===
                                            "year"
                                                ? $t("vue.switch_to_monthly")
                                                : $t("vue.switch_to_yearly")
                                        }}</span
                                    >
                                </div>
                            </div>
                            <div class="mt-2 grid grid-cols-2">
                                <div class="font-bold">
                                    {{ $t("vue.next_invoice") }}
                                </div>
                                <div
                                    :class="
                                        currentPaddleSubscription?.next_billing_at
                                            ? 'text-gray-600'
                                            : 'text-red-500'
                                    "
                                >
                                    {{
                                        currentPaddleSubscription?.next_billing_at
                                            ? new Date(
                                                  currentPaddleSubscription?.next_billing_at.date,
                                              ).toLocaleDateString("en-US", {
                                                  day: "numeric",
                                                  month: "long",
                                                  year: "numeric",
                                              })
                                            : currentPaddleSubscription
                                                    ?.currentBillingPeriod
                                                    ?.start
                                              ? new Date(
                                                    currentPaddleSubscription?.currentBillingPeriod.start.date,
                                                ).toLocaleDateString("en-US", {
                                                    day: "numeric",
                                                    month: "long",
                                                    year: "numeric",
                                                })
                                              : "N/A"
                                    }}
                                </div>
                            </div>
                            <div class="mt-2 grid grid-cols-2">
                                <div class="font-bold">
                                    {{ $t("vue.next_payment") }}
                                </div>
                                <div class="text-gray-600">
                                    ${{
                                        currentPaddleSubscription?.totals?.total
                                    }}
                                </div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex h-full flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div class="text-5xl font-bold">
                                {{ domainsCount }}
                            </div>
                            <div>{{ $t("vue.your_domains") }}</div>
                            <AtomicButton
                                class="mt-4"
                                color="colorless"
                                @click="visitCustomerPortal()"
                            >
                                {{ $t("vue.manage_subscription_invoices") }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="mt-5 grid-cols-layout border-b border-gray-300 pb-5 lg:grid"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ $t("vue.available_dmarc_subscription") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ $t("vue.check_pricing_page_info") }}
                    </p>
                </div>

                <div class="flex grow flex-col gap-6">
                    <!-- Loading state for plans -->
                    <div
                        v-if="isLoading"
                        class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                    >
                        <div class="grid h-full w-full grid-cols-12 p-[19px]">
                            <div class="col-span-7 px-6 py-4">
                                <div
                                    class="h-8 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div class="mt-6 space-y-2">
                                    <div
                                        class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                    ></div>
                                    <div
                                        class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                    ></div>
                                    <div
                                        class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                    ></div>
                                </div>
                            </div>
                            <div
                                class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                            >
                                <div
                                    class="h-8 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="mt-4 h-12 w-16 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="mt-2 h-4 w-20 animate-pulse rounded bg-gray-200"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-for="(plan, key) in groupedPlansByProduct"
                        :key="key"
                        class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                    >
                        <div class="grid h-full w-full grid-cols-12 p-[19px]">
                            <div class="col-span-7 px-6 py-4">
                                <h2 class="text-3xl font-bold">
                                    {{
                                        plan[0].productName
                                            .replace(/DMARC\/|eye\s*-\s*/g, "")
                                            .trim()
                                    }}
                                </h2>
                                <p class="text-md mt-2">
                                    {{ plan[0].productDescription }}
                                </p>
                                <div class="mt-6 grid grid-cols-2">
                                    <div class="font-bold">Payment</div>
                                    <div class="text-gray-600">
                                        ${{
                                            payYearly
                                                ? plan[1].totalPrice
                                                : plan[0].totalPrice
                                        }}/{{ payYearly ? "year" : "month" }}
                                        per
                                        {{
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    "",
                                                )
                                                .trim() === "Basic"
                                                ? "plan"
                                                : "domain"
                                        }}
                                    </div>
                                </div>
                                <div class="mt-2 grid grid-cols-2">
                                    <div class="font-bold">
                                        {{
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    "",
                                                )
                                                .trim() === "Basic"
                                                ? "Maximum domains"
                                                : "Domains"
                                        }}
                                    </div>
                                    <div class="text-gray-600">
                                        {{
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    "",
                                                )
                                                .trim() === "Basic"
                                                ? "1"
                                                : "2 - 50"
                                        }}
                                    </div>
                                </div>
                                <div class="mt-2 grid grid-cols-2">
                                    <div class="font-bold">Data history</div>
                                    <div class="text-gray-600">
                                        {{
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    "",
                                                )
                                                .trim() === "Basic"
                                                ? `30 ${$t("vue.days")}`
                                                : `1 ${$t("vue.year")}`
                                        }}
                                    </div>
                                </div>
                            </div>
                            <div
                                class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                            >
                                <div class="flex gap-3">
                                    <Switch
                                        v-model="payYearly"
                                        :left-label="$t('vue.pay_monthly')"
                                        :right-label="$t('vue.pay_yearly')"
                                    />
                                </div>
                                <div
                                    class="mt-8 flex flex-col items-center justify-center"
                                >
                                    <div class="text-5xl font-bold">
                                        ${{
                                            payYearly
                                                ? plan[1].totalPrice / 12
                                                : plan[0].totalPrice
                                        }}
                                    </div>
                                    <div class="mt-2">
                                        {{
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    "",
                                                )
                                                .trim() !== "Basic"
                                                ? `per domain/${$t(
                                                      "vue.month",
                                                  )}`
                                                : `per ${$t("vue.month")}`
                                        }}
                                    </div>
                                </div>
                                <div class="group relative">
                                    <AtomicButton
                                        v-if="isSubscribed"
                                        class="mt-4"
                                        :disabled="
                                            isBasicPlanDisabled &&
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    '',
                                                )
                                                .trim() === 'Basic'
                                        "
                                        @click="switchToPlan(plan)"
                                    >
                                        {{ $t("vue.switch_to_this_plan") }}
                                    </AtomicButton>
                                    <AtomicButton
                                        v-else
                                        class="mt-4"
                                        :disabled="
                                            isBasicPlanDisabled &&
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    '',
                                                )
                                                .trim() === 'Basic'
                                        "
                                        @click="handlePlanSelection(plan)"
                                    >
                                        {{ $t("vue.change_plan") }}
                                    </AtomicButton>

                                    <!-- Tooltip for disabled Basic plan -->
                                    <div
                                        v-if="
                                            isBasicPlanDisabled &&
                                            plan[0].productName
                                                .replace(
                                                    /DMARC\/|eye\s*-\s*/g,
                                                    '',
                                                )
                                                .trim() === 'Basic'
                                        "
                                        class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 transform opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                                    >
                                        <div
                                            class="whitespace-nowrap rounded bg-gray-800 px-3 py-2 text-xs text-white"
                                        >
                                            {{
                                                $t(
                                                    "vue.basic_plan_limited_message",
                                                )
                                            }}
                                            {{ $t("vue.you_currently_have") }}
                                            {{ domainsCount }} domains.
                                            <div class="tooltip-triangle"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        DMARCeye for big teams
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        Reach out to our sales team if you're interested in a
                        tailored offer for a large number of domains.
                    </p>
                </div>

                <!-- Loading skeleton for DMARCeye for big teams -->
                <div
                    v-if="isLoading"
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <div
                                class="h-8 w-32 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div class="mt-6 space-y-2">
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div
                                class="h-8 w-32 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-4 h-12 w-16 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-20 animate-pulse rounded bg-gray-200"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Enterprise -->
                <div
                    v-if="!isLoading"
                    class="mx-auto min-h-[265px] w-full rounded-md bg-white shadow-card"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <h2 class="text-3xl font-bold">Enterprise</h2>
                            <p class="text-md mt-2">
                                For 50+ domains. Includes extended data
                                retention and enhanced support.
                            </p>
                            <div class="mt-6 grid grid-cols-2">
                                <div class="font-bold">Domains</div>
                                <div class="text-gray-600">50+</div>
                            </div>
                            <div class="mt-2 grid grid-cols-2">
                                <div class="font-bold">Data History</div>
                                <div class="text-gray-600">unlimited</div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-xl border bg-gray-100"
                        >
                            <div class="text-5xl font-bold">
                                <span class="text-2xl">from</span> $300
                            </div>
                            <div>per month</div>
                            <a
                                href="https://zapti.me/dmarceye/sales"
                                target="_blank"
                            >
                                <AtomicButton class="mt-6" color="black">
                                    Contact Sales
                                </AtomicButton>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <SubscriptionBuyModal
            v-if="selectedPlan"
            :paddle-client-token="props.paddleClientToken"
            :selected-plan="selectedPlan"
            :paddle-environment="props.paddleEnvironment"
            :domains-count="domainsCountToBuy"
            :period="payYearly ? 'yearly' : 'monthly'"
            @close="handleCloseSubscriptionModal"
        />

        <SubscriptionSwitchFrequencyModal
            v-if="showFrequencySwitchModal"
            :current-frequency="currentFrequency"
            :current-price="currentPaddleSubscription?.totals?.total || '0'"
            :current-plan-name="
                currentPaddleSubscription?.items?.[0]?.productName
                    ?.replace(/DMARC\/|eye\s*-\s*/g, '')
                    .trim() || 'Unknown Plan'
            "
            :next-billing-date="
                currentPaddleSubscription?.next_billing_at?.date || ''
            "
            @close="handleFrequencyModalClose"
            @success="handleFrequencySuccess"
        />

        <SubscriptionSwitchPlanModal
            v-if="showPlanSwitchModal && selectedPlanForSwitch"
            :current-frequency="currentFrequency"
            :current-price="currentPaddleSubscription?.totals?.total || '0'"
            :current-plan-name="
                currentPaddleSubscription?.items?.[0]?.productName
                    ?.replace(/DMARC\/|eye\s*-\s*/g, '')
                    .trim() || 'Unknown Plan'
            "
            :next-billing-date="
                currentPaddleSubscription?.next_billing_at?.date || ''
            "
            :new-plan-price-id="selectedPlanForSwitch.priceId"
            :new-plan-name="selectedPlanForSwitch.planName"
            :new-frequency="selectedPlanForSwitch.frequency"
            @close="handlePlanSwitchModalClose"
            @success="handlePlanSwitchSuccess"
        />
    </BillingTabs>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { type IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription";
import route from "ziggy-js";
import axios from "axios";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const props = defineProps({
    isTrialing: {
        type: Boolean,
        default: false,
    },
    isSubscribed: {
        type: Boolean,
        default: false,
    },
    trialEndsAt: {
        type: String,
        default: "",
    },
    domainsCount: {
        type: Number,
        default: () => 0,
    },
    paddleClientToken: {
        type: String,
        default: "",
    },
    paddleEnvironment: {
        type: String as () => "production" | "sandbox",
        default: "production",
    },
    paddleSubscription: {
        type: Object as PropType<IPaddleSubscription | null>,
        default: null,
    },
    paddleCustomerPortalUrl: {
        type: String,
        default: "",
    },
    plans: {
        type: Array as () => {
            type: "monthly" | "yearly";
            priceId: string;
            productId: string;
            productName: string;
            productDescription: string;
            totalPrice?: number;
        }[],
        default: () => [],
    },
});

// Reactive data for async loading
const isLoading = ref(true);
const asyncPaddleSubscription = ref<IPaddleSubscription | null>(null);
const asyncPaddleCustomerPortalUrl = ref<string>("");
const asyncPlans = ref<typeof props.plans>([]);

// Use async data if available, fallback to props
const currentPaddleSubscription = computed(
    () => asyncPaddleSubscription.value || props.paddleSubscription,
);
const currentPaddleCustomerPortalUrl = computed(
    () => asyncPaddleCustomerPortalUrl.value || props.paddleCustomerPortalUrl,
);
const currentPlans = computed(() =>
    asyncPlans.value.length > 0 ? asyncPlans.value : props.plans,
);
const payYearly = ref(true);
const inertiaProps = usePage<IRhizomPageProps>();
const hasGodMode = computed(() => inertiaProps.props.hasGodMode);

// Async loading functions
const loadSubscriptionData = async () => {
    try {
        const response = await axios.get(route("billing.subscriptions.data"));
        asyncPaddleSubscription.value = response.data.paddleSubscription;
        asyncPaddleCustomerPortalUrl.value =
            response.data.paddleCustomerPortalUrl;
    } catch (error) {
        console.error("Failed to load subscription data:", error);
    }
};

const loadPlans = async () => {
    try {
        const response = await axios.get(route("billing.subscriptions.plans"));
        asyncPlans.value = response.data.plans;
    } catch (error) {
        console.error("Failed to load plans:", error);
    }
};

const loadAllData = async () => {
    isLoading.value = true;

    // Load data in parallel
    await Promise.all([loadSubscriptionData(), loadPlans()]);

    isLoading.value = false;
};

const selectedPlan = ref(
    undefined as {
        type: "monthly" | "yearly";
        priceId: string;
        productId: string;
        totalPrice?: number;
        productName: string;
        productDescription: string;
    }[],
);

const showFrequencySwitchModal = ref(false);
const showPlanSwitchModal = ref(false);
const selectedPlanForSwitch = ref<{
    priceId: string;
    planName: string;
    frequency: "month" | "year";
} | null>(null);

const handleCloseSubscriptionModal = () => {
    selectedPlan.value = undefined;
    window.location.reload();
};

const handlePlanSelection = (plan: (typeof currentPlans.value)[0][]) => {
    // Prevent selecting Basic plan if user has more than 1 domain
    if (
        isBasicPlanDisabled.value &&
        plan[0].productName.replace(/DMARC\/|eye\s*-\s*/g, "").trim() ===
            "Basic"
    ) {
        return;
    }

    selectedPlan.value = plan;
};

const switchToPlan = (plan: (typeof currentPlans.value)[0][]) => {
    // Prevent switching to Basic plan if user has more than 1 domain
    if (
        isBasicPlanDisabled.value &&
        plan[0].productName.replace(/DMARC\/|eye\s*-\s*/g, "").trim() ===
            "Basic"
    ) {
        return;
    }

    let planToSwitch;
    if (payYearly.value) {
        planToSwitch = plan[1];
    } else {
        planToSwitch = plan[0];
    }

    // Show the plan switch modal instead of directly switching
    selectedPlanForSwitch.value = {
        priceId: planToSwitch.priceId,
        planName: planToSwitch.productName
            .replace(/DMARC\/|eye\s*-\s*/g, "")
            .trim(),
        frequency: planToSwitch.type === "yearly" ? "year" : "month",
    };
    showPlanSwitchModal.value = true;
};

const groupedPlansByProduct = computed(() => {
    // Filter out the plan that user is currently subscribed to
    const filteredPlans = currentPlans.value.filter((plan) => {
        if (!props.isSubscribed || !currentPaddleSubscription.value) {
            return true; // Show all plans if not subscribed
        }

        // Check if this plan matches the current subscription by product name
        const currentProductName =
            currentPaddleSubscription.value.items[0]?.productName;
        return plan.productName !== currentProductName;
    });

    return filteredPlans.reduce(
        (acc, plan) => {
            if (!acc[plan.productId]) {
                acc[plan.productId] = [];
            }
            acc[plan.productId].push(plan);
            return acc;
        },
        {} as Record<string, typeof currentPlans.value>,
    );
});

const isBasicPlanDisabled = computed(() => {
    return props.domainsCount > 1;
});

const getBasicPlanTooltip = computed(() => {
    if (props.domainsCount > 1) {
        return `Basic plan is limited to 1 domain. You currently have ${props.domainsCount} domains. Please upgrade to Scale plan for multiple domains.`;
    }
    return "";
});

const domainsCountToBuy = computed(() => {
    if (
        selectedPlan.value !== undefined &&
        selectedPlan.value[0].productName
            .replace(/DMARC\/|eye\s*-\s*/g, "")
            .trim() === "Scale"
    ) {
        return props.domainsCount < 2 ? 2 : props.domainsCount;
    }

    return props.domainsCount === 0 ? 1 : props.domainsCount;
});

const currentFrequency = computed(() => {
    return (currentPaddleSubscription.value?.period as string) || "month";
});

function visitCustomerPortal() {
    if (currentPaddleCustomerPortalUrl.value) {
        window.open(currentPaddleCustomerPortalUrl.value, "_self");
    } else {
        console.error("Paddle customer portal URL is not set.");
    }
}

function updateFrequency() {
    showFrequencySwitchModal.value = true;
}

const handleFrequencyModalClose = () => {
    showFrequencySwitchModal.value = false;
    // Refresh subscription data after frequency switch
    loadSubscriptionData();
};

const handlePlanSwitchModalClose = () => {
    showPlanSwitchModal.value = false;
    selectedPlanForSwitch.value = null;
    // Refresh subscription data after plan switch
    loadSubscriptionData();
};

const handleFrequencySuccess = () => {
    // Refresh subscription data after successful frequency switch
    loadSubscriptionData();
};

const handlePlanSwitchSuccess = () => {
    // Refresh subscription data after successful plan switch
    loadSubscriptionData();
};

// Load data on component mount
onMounted(() => {
    loadAllData();
});
</script>

<style scoped>
.tooltip-triangle {
    width: 14px;
    height: 14px;
    background: linear-gradient(to bottom right, transparent 55%, #1f2937 45%);
    transform: rotate(45deg);
    position: absolute;
    border-radius: 2px;
    bottom: -6px;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 2;
}
</style>
